import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { Stack } from '@mantine/core'
import MainTable from 'app/views/components/Table/MainTable'
import KPI_Section from 'app/views/components/KPI/KPI-horizontal-section'
import {
  getServicingLoader,
  getServicingRows,
} from 'app/state/ducks/servicing/selectors'
import { formatNumberToCurrency, stringToDate } from 'app/utils/util-functions'
import {
  GridColumnVisibilityStorage,
  GridPaginationStorage,
  GridSortingStorage,
  useGridColumnVisibility,
  useGridPagination,
  useGridSorting,
} from 'app/utils/grid-sorting-storage'
import { ServicingCols } from '../servicing/servicing-columnDefs'
import { useServicingFeed } from '../servicing/use-servicing-feed'
import { myworkKPIData } from './mywork-kpi-data'
import { calcTotalAmount } from './mywork-util/my-work-utils'
import {
  MYWORK_UNSCHEDULED_TYPES,
  filterMyWorkRows,
  myWorkFeedUpperBound,
} from './mywork-util/servicing-feed-window'

interface props {
  /** False until the business date has settled: the feed is not read on the today-fallback (LOS-7219). */
  dateReady?: boolean
  date: Date
  useDateRange?: boolean
  dateRange?: [Date, Date]
}

/**
 * My Work → Unscheduled: new borrowings, principal payments and PIKs from the
 * servicing feed, trimmed to the picked range (or the business date) as
 * before. The 7 list loads are gone.
 */
export function UnscheduledPage({ date, useDateRange, dateRange, dateReady = true }: props) {
  const rows = useSelector(getServicingRows)
  const isLoading = useSelector(getServicingLoader)

  const to = myWorkFeedUpperBound(date, useDateRange, dateRange)
  useServicingFeed({ to }, { enabled: dateReady })

  const { sorting, handleSortingChange } = useGridSorting(
    GridSortingStorage.myworkUnscheduled,
  )
  const { pagination, handlePaginationChange } = useGridPagination(
    GridPaginationStorage.myworkUnscheduled,
  )
  const { columnVisibility, handleColumnVisibilityChange } =
    useGridColumnVisibility(GridColumnVisibilityStorage.myworkUnscheduled)

  const unscheduledFiltered = useMemo(
    () =>
      filterMyWorkRows(
        rows.filter(row => MYWORK_UNSCHEDULED_TYPES.includes(row.type)),
        date,
        useDateRange,
        dateRange,
      ),
    [rows, date, useDateRange, dateRange],
  )

  const deadline =
    stringToDate(
      unscheduledFiltered[0]?.effectiveDate.toString(),
    )?.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    }) ?? 'N/A'
  const totalAmount =
    formatNumberToCurrency(
      calcTotalAmount(
        // a fee still being priced, or that could not be, has no amount to add
        unscheduledFiltered
          .filter(item => !item?.amountPending)
          .map(item => item?.amount ?? 0),
      ) ?? 0,
      'USD',
    )?.toString() ?? 'N/A'
  const kpiData = myworkKPIData({
    activityCount: unscheduledFiltered.length,
    percentageComplete: 'N/A',
    deadlineApproaching: deadline === 'Invalid Date' ? 'N/A' : deadline,
    totalActivityAmount: totalAmount,
  })

  return (
    <Stack w="100%">
      <KPI_Section kpiData={kpiData} isLoading={isLoading} />
      <MainTable
        tableName="Unscheduled Table"
        isLoading={isLoading}
        withPagination={true}
        csvExportEnabled={true}
        enableTopToolbar={false}
        headerBackgroundColor="#F0EEEE"
        overflow="hidden"
        minHeight="0px"
        columnDefs={ServicingCols}
        data={unscheduledFiltered}
        initialSorting={[
          { id: sorting.sortBy, desc: sorting.order === 'desc' },
        ]}
        onSortingChange={handleSortingChange}
        initialPagination={pagination}
        onPaginationChange={handlePaginationChange}
        initialColumnVisibility={columnVisibility}
        onColumnVisibilityChange={handleColumnVisibilityChange}
        // Unscheduled rows never navigated on click.
        setRow={() => undefined}
      />
    </Stack>
  )
}
